import React, { useState, useMemo, useEffect, memo } from 'react';
import {
    Search, Plus, Edit, Trash2, Save, X, RotateCw, Box, Hash, Tag, Scale
} from 'lucide-react';

// Import necessary Reactstrap components
import { 
    Container, Row, Col, Card, CardBody, Table, 
    Button, FormGroup, Input, Alert, InputGroup, InputGroupText
} from 'reactstrap';

// Mock component placeholder (as provided by you)
const Breadcrumbs = ({ title, breadcrumbItem }) => (
    <div className="mb-4">
        <h4 className="card-title">{title}</h4>
        <ol className="breadcrumb m-0">
            <li className="breadcrumb-item">{breadcrumbItem}</li>
        </ol>
    </div>
);

// --- API CONFIGURATION ---
const API_BASE_URL = 'https://api.hvacsync.in/api/compressor-parameters/'; 
const API_DELAY = 500; // Simulated network delay

// --- API INTERACTION LAYER (Refactored to accept Token) ---

/**
 * Helper function to handle fetch calls, including error handling, JSON parsing, 
 * and required Authorization header using a dynamically fetched token.
 * * @param {string} url - The endpoint path (relative to API_BASE_URL).
 * @param {string} method - HTTP method ('GET', 'POST', 'PUT', 'DELETE').
 * @param {object|null} data - Body data for POST/PUT requests.
 * @param {string} token - The authorization token.
 */
const apiCall = async (url, method = 'GET', data = null, token) => {
    // Check if token is available
    if (!token) {
        throw new Error("Authorization token not found. Please log in.");
    }

    // 1. Simulate Network Delay (Remove this in production)
    await new Promise(resolve => setTimeout(resolve, API_DELAY)); 
    
    const options = {
        method,
        headers: {
            'Content-Type': 'application/json',
            // CORRECTED: Authorization header is correctly applied here
            'Authorization': `Bearer ${token}`,
        },
    };

    if (data) {
        options.body = JSON.stringify(data);
    }
    
    const fullUrl = `${API_BASE_URL}${url}`;
    const response = await fetch(fullUrl, options); 

    // Handle non-200 responses
    if (!response.ok) {
        // Attempt to parse JSON error message first, fallback to text
        const errorText = await response.text();
        let errorMessage = `API Error ${response.status} (${response.statusText}): ${errorText}`;
        
        try {
            const errorJson = JSON.parse(errorText);
            // Assuming API returns a standard 'detail' or 'error' key
            errorMessage = errorJson.detail || errorJson.error || errorMessage; 
        } catch (e) {
            // If it's not JSON, use the plain text error message
        }
        
        throw new Error(errorMessage);
    }

    // Handle 204 No Content (like successful DELETE)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null; 
    }

    return response.json();
};


// --- API FUNCTIONS FACTORY (Requires token to be injected) ---
const createParameterApi = (token) => ({
    // GET: api/compressor-parameters/
    fetchParameters: async () => {
        return apiCall('', 'GET', null, token); 
    },

    // POST/PUT: api/compressor-parameters/
    saveParameter: async (parameterData) => {
        // ID check logic remains the same
        const isCreating = parameterData.id < 0 || typeof parameterData.id === 'string'; 
        
        // Use destructuring assignment for cleaner object creation
        const { errors, ...dataToSend } = parameterData;
        
        if (isCreating) {
            // POST for creation (exclude the temporary negative ID from payload)
            const { id, ...createPayload } = dataToSend;
            return apiCall('', 'POST', createPayload, token); 
        } else {
            // PUT/PATCH for update
            return apiCall(`${parameterData.id}`, 'PUT', dataToSend, token); 
        }
    },

    // DELETE: api/compressor-parameters/{id}
    deleteParameter: async (id) => {
        return apiCall(`${id}`, 'DELETE', null, token);
    }
});


// --- HELPER FUNCTIONS (Remains the same) ---
const validateParameterData = (data) => {
    const errors = {};
    if (!data.name) {
        errors.name = 'Key (min 3 chars, lowercase, numbers, and underscore only).';
    }
    if (!data.display_name || data.display_name.length < 3) {
        errors.display_name = 'Display Name is required (min 3 chars).';
    }
    if (!data.unit || data.unit.length < 1) {
        errors.unit = 'Unit is required.';
    }
    if (!Number.isInteger(Number(data.index)) || Number(data.index) < 1) {
        errors.index = 'Index must be a positive integer.';
    }
    return errors;
};

// --- PARAMETER ROW COMPONENT (Same as before, good to keep memoized) ---
const ParameterRow = memo(({ 
    parameter, 
    isEditing, 
    isSaving, 
    tempData, 
    handleInputChange, 
    handleSave, 
    handleCancel, 
    handleEdit, 
    handleDelete 
}) => {
    const data = isEditing ? tempData : parameter;
    const errors = data.errors || {};
    const isRowSaving = isEditing && isSaving; 

    // ... (JSX for the table row remains identical)
    return (
        <tr className={isEditing ? 'table-info' : ''}>
            <th scope="row" className="align-middle">
                {isEditing ? (
                    <FormGroup className="mb-0">
                        <Input type="text" name="name" value={data.name} onChange={handleInputChange} invalid={!!errors.name} disabled={isRowSaving} autoFocus={data.id < 0} placeholder="e.g., pressure_l1"/>
                        {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                    </FormGroup>
                ) : (<code className="text-secondary">{data.name}</code>)}
            </th>
            <td className="align-middle">
                {isEditing ? (
                    <FormGroup className="mb-0">
                        <Input type="text" name="display_name" value={data.display_name} onChange={handleInputChange} invalid={!!errors.display_name} disabled={isRowSaving} placeholder="e.g., Pressure Line 1"/>
                         {errors.display_name && <div className="invalid-feedback d-block">{errors.display_name}</div>}
                    </FormGroup>
                ) : (<strong>{data.display_name}</strong>)}
            </td>
            <td className="align-middle">
                {isEditing ? (
                    <FormGroup className="mb-0">
                        <Input type="text" name="unit" value={data.unit} onChange={handleInputChange} invalid={!!errors.unit} disabled={isRowSaving} placeholder="e.g., bar, °C, RPM"/>
                         {errors.unit && <div className="invalid-feedback d-block">{errors.unit}</div>}
                    </FormGroup>
                ) : (<span className="text-muted">{data.unit}</span>)}
            </td>
            <td className="align-middle">
                {isEditing ? (
                    <FormGroup className="mb-0">
                        <Input type="number" name="index" value={data.index} onChange={handleInputChange} invalid={!!errors.index} disabled={isRowSaving} min="1"/>
                         {errors.index && <div className="invalid-feedback d-block">{errors.index}</div>}
                    </FormGroup>
                ) : (<span className="badge bg-light text-dark border p-2">{data.index}</span>)}
            </td>
            <td className="text-end align-middle">
                <div className="d-flex justify-content-end gap-2">
                    {isEditing ? (
                        <>
                            <Button color="primary" onClick={handleSave} disabled={isRowSaving} title="Save Changes" size="sm">
                                {isRowSaving ? <RotateCw className="icon-xs spin" /> : <Save className="icon-xs" />}
                            </Button>
                            <Button color="secondary" onClick={() => handleCancel(parameter.id)} disabled={isRowSaving} title="Cancel Edit" size="sm">
                                <X className="icon-xs" />
                            </Button>
                        </>
                    ) : (
                        <>
                        
                            <Button color="warning" onClick={() => handleEdit(parameter)} disabled={isSaving} title="Edit" size="sm">
                               Edit
                            </Button>
                            <Button color="danger" onClick={() => handleDelete(parameter)} disabled={isSaving} title="Delete" size="sm">
                                Delete
                            </Button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
});
ParameterRow.displayName = 'ParameterRow';


// --- MAIN COMPONENT ---
const CompressorParameter= () => { // Renamed for clarity
    const [parameters, setParameters] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [tempParameterData, setTempParameterData] = useState({});
    const [nextTempId, setNextTempId] = useState(-1); 
    const [isSaving, setIsSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });

    // FIX 1: Access the token dynamically within the component
    const authToken = useMemo(() => localStorage.getItem("access"), []);
    
    // Use useMemo to create the API object only when the token changes
    const parameterApi = useMemo(() => createParameterApi(authToken), [authToken]);

    // --- INITIAL DATA LOAD ---
    const loadData = async () => {
        // Prevent loading if no token is available
        if (!authToken) {
            setIsLoading(false);
            setStatusMessage({ type: 'error', message: 'Authentication required. Please check your login session.' });
            return;
        }

        setIsLoading(true);
        setStatusMessage({});
        try {
            const data = await parameterApi.fetchParameters();
            setParameters(data);
            setStatusMessage({ type: 'success', message: `Successfully loaded ${data.length} parameters.` });
        } catch (error) {
            console.error("Fetch Error:", error);
            // Use the detailed error message from apiCall
            setStatusMessage({ type: 'error', message: error.message || 'Failed to load parameters from API.' });
        } finally {
            setIsLoading(false);
        }
    };

    // FIX 2: Use useEffect for data loading
    useEffect(() => {
        loadData();
    }, [authToken]); // Dependency added to re-load if token somehow changes

    // --- CRUD ACTIONS (Logic remains the same, relying on parameterApi calls) ---

    const handleAddRow = () => {
        if (editingId !== null) {
            setStatusMessage({ type: 'warning', message: 'Please save or cancel the current row before adding another.' });
            return;
        }
        
        const newId = nextTempId;
        const newParameter = {
            id: newId,
            name: '',
            display_name: '',
            unit: '',
            index: parameters.length > 0 ? parameters.reduce((max, p) => Math.max(max, p.index), 0) + 1 : 1, 
        };
        
        setParameters(prev => [newParameter, ...prev]); 
        setEditingId(newId);
        setTempParameterData(newParameter);
        setNextTempId(prev => prev - 1);
        setStatusMessage({});
    };

    const handleEdit = (parameter) => {
        if (editingId !== null) {
            setStatusMessage({ type: 'warning', message: 'Please save or cancel the row currently being edited.' });
            return;
        }
        setEditingId(parameter.id);
        setTempParameterData({ ...parameter });
        setStatusMessage({});
    };

    const handleSave = async () => {
        const cleanedData = { ...tempParameterData };
        const errors = validateParameterData(cleanedData);

        if (Object.keys(errors).length > 0) {
            setTempParameterData(prev => ({ ...cleanedData, errors }));
            setStatusMessage({ type: 'error', message: 'Please correct the validation errors.' });
            return;
        }

        setIsSaving(true);
        const isCreating = editingId < 0;

        try {
            const savedParameter = await parameterApi.saveParameter(cleanedData);
            
            setParameters(prev => {
                const updatedList = prev.filter(p => p.id !== editingId);
                return [savedParameter, ...updatedList].sort((a, b) => a.index - b.index);
            });
            
            setStatusMessage({ 
                type: 'success', 
                message: `Parameter '${savedParameter.display_name}' successfully ${isCreating ? 'added' : 'updated'}.` 
            });

        } catch (error) {
             console.error("Save Error:", error);
             setStatusMessage({ type: 'error', message: `Failed to save parameter: ${error.message || 'Unknown error'}` });
        } finally {
            setIsSaving(false);
            setEditingId(null);
            setTempParameterData({});
        }
    };

    const handleCancel = (id) => {
        if (id < 0) {
            setParameters(prev => prev.filter(p => p.id !== id));
        }
        setEditingId(null);
        setTempParameterData({});
        setStatusMessage({});
    };
    
    const handleDelete = async (parameter) => {
        if (editingId !== null) {
             setStatusMessage({ type: 'warning', message: 'Please save or cancel the row currently being edited before deleting another.' });
            return;
        }
        if (window.confirm(`Are you sure you want to delete parameter '${parameter.display_name}'? This action cannot be undone.`)) {
            setIsSaving(true);
            try {
                await parameterApi.deleteParameter(parameter.id);
                setParameters(prev => prev.filter(p => p.id !== parameter.id));
                setStatusMessage({ type: 'success', message: `Parameter '${parameter.display_name}' deleted successfully.` });
            } catch (error) {
                console.error("Delete Error:", error);
                setStatusMessage({ type: 'error', message: `Failed to delete parameter: ${error.message || 'Unknown error'}` });
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        const processedValue = type === 'number' ? (value === '' ? '' : Number(value)) : value;

        setTempParameterData(prev => ({ 
            ...prev, 
            [name]: processedValue, 
            errors: { ...prev.errors, [name]: undefined } 
        }));
    };

    // --- FILTERED AND SORTED DATA (Logic unchanged) ---
    const filteredAndSortedParameters = useMemo(() => {
        let currentList = parameters;
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            currentList = currentList.filter(p => 
                p.name.toLowerCase().includes(lowerCaseQuery) ||
                p.display_name.toLowerCase().includes(lowerCaseQuery) ||
                p.unit.toLowerCase().includes(lowerCaseQuery)
            );
        }
        return currentList.sort((a, b) => {
            if (a.index !== b.index) {
                return a.index - b.index;
            }
            return a.id - b.id; 
        });
    }, [parameters, searchQuery]);
document.title = "Parameters | HVACSYNC ";
    // --- MAIN RENDER (Unchanged) ---
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                   
                
                    <Card className="shadow-lg">
                        <CardBody>
                            <Row className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                                <Col xs="auto">
                                    <h1 className="h3 font-weight-bold text-dark mb-0 d-flex align-items-center">
                                       
                                        Compressor Parameters 
                                    </h1>
                                   
                                </Col>
                                <Col xs="auto">
                                    <Button
                                        color="primary"
                                        onClick={handleAddRow}
                                        className="d-flex align-items-center"
                                        disabled={editingId !== null || isSaving}
                                    >
                                        <Plus size={20} className="me-2" />
                                        Add New Parameter
                                    </Button>
                                </Col>
                            </Row>
                            
                            {/* Status Message Area */}
                            {/* {statusMessage.message && (
                                <Alert 
                                    className="mb-4"
                                    color={
                                        statusMessage.type === 'success' ? 'success' : 
                                        statusMessage.type === 'error' ? 'danger' : 'warning'
                                    }
                                >
                                    {statusMessage.message}
                                </Alert>
                            )} */}

                            {/* Search Row */}
                            <Row className="mb-4">
                                <Col md={6}>
                                    <InputGroup>
                                        <Input
                                            type="text"
                                            placeholder="Search by key, display name, or unit..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            disabled={isSaving || editingId !== null || isLoading}
                                        />
                                        <InputGroupText>
                                            <Search size={18} />
                                        </InputGroupText>
                                    </InputGroup>
                                </Col>
                            </Row>

                            {/* Parameters Table */}
                            <div className="table-responsive">
                                <Table striped hover responsive className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th><Tag size={16} className="me-1" /> Internal Key</th>
                                            <th><Box size={16} className="me-1" /> Display Name</th>
                                            <th><Scale size={16} className="me-1" /> Unit</th>
                                            <th><Hash size={16} className="me-1" /> Index</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                             <tr>
                                                <td colSpan="5" className="text-center text-primary py-4">
                                                    <RotateCw className="icon-lg spin me-2" /> Loading parameters...
                                                </td>
                                            </tr>
                                        ) : filteredAndSortedParameters.length > 0 ? (
                                            filteredAndSortedParameters.map((parameter) => (
                                                <ParameterRow
                                                    key={parameter.id} 
                                                    parameter={parameter}
                                                    isEditing={editingId === parameter.id}
                                                    isSaving={isSaving}
                                                    tempData={tempParameterData}
                                                    handleInputChange={handleInputChange}
                                                    handleSave={handleSave}
                                                    handleCancel={handleCancel}
                                                    handleEdit={handleEdit}
                                                    handleDelete={handleDelete}
                                                />
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted">
                                                    No parameters found. Click 'Add New Parameter' to start.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default CompressorParameter;